import React, { useCallback, useState, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useFormState } from 'react-hook-form'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import {
  text,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import {
  doSaveChannelDraft,
  doClearChannelDraft,
  doAddGmailChannel,
  doOauthGmailChannel,
} from 'ducks/channels/actions'
import { useConfirmHoldsCallback } from 'util/dirtyHolds'
import { oAuthErrorToErrorCode } from 'subapps/settings/components/drawers/Channels/ChannelDetails/oAuthError'
import ChannelDetailsForm from 'subapps/settings/components/shared/forms/ChannelDetails'
import CONFIG from '../config'
import { CHANNEL_EMAIL_TYPES } from '../../Channels.data'
import { styles } from '../styles'

const Actions = ({
  formId,
  channelType,
  onClose,
  isInstalling,
  errorCode,
  control,
}) => {
  const { name, icon } = CHANNEL_EMAIL_TYPES[channelType]
  const { oAuthErrors } = CONFIG[channelType]

  const { isValid } = useFormState({ control })
  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const oauthErrorMessage = useMemo(
    () => {
      if (!errorCode) return null

      return oAuthErrors[errorCode] || oAuthErrors.DEFAULT
    },
    [errorCode, oAuthErrors]
  )

  const saveBtnText = (
    <>
      <span css={styles.btnIcon} className="grui mr-4">
        {icon}
      </span>
      Authenticate with {name}
    </>
  )

  return (
    <>
      {oauthErrorMessage && (
        <MessageCard
          type="negative"
          closeable
          className="grui mx-12 my-10"
          css={styles.messageCard}
          isIconHidden
        >
          <p css={[paragraph.styles.preflight, text.styles.fontMedium]}>
            Authentication error
          </p>
          <p css={paragraph.styles.preflight} className="grui mt-4">
            {oauthErrorMessage}
          </p>
        </MessageCard>
      )}
      <ModalBtns
        saveBtnText={saveBtnText}
        saveBtnHtmlType="submit"
        saveBtnTestId="authenticate-google-button"
        saveBtnDisabled={!isValid || isInstalling}
        saveBtnForm={formId}
        tertiaryBtnText="Back"
        onClickTertiaryBtn={handleBack}
      />
    </>
  )
}

// shares some functionality with drawers/Channels/AuthenticateChannel
const ChannelDetailsOAuth = ({
  onClose,
  onExit,
  drawerChannelType: channelType = 'forwarding',
  drawerResourceId: channelId,
  drawerSource: source = 'settings',
  drawerId,
}) => {
  const dispatch = useDispatch()
  const [errorCode, setErrorCode] = useState(null)
  const [isInstalling, setIsInstalling] = useState(false)
  const { drawer, drawerTitle } = CONFIG[channelType]

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer(
    drawer
  )

  const formId = `${drawerId}-form`

  const handleOnClose = useConfirmHoldsCallback(formId, onClose, [onClose])

  const handleOnExit = useConfirmHoldsCallback(
    null,
    () => {
      dispatch(doClearChannelDraft(channelId))
      onExit()
    },
    [dispatch, onExit, channelId]
  )

  const handleError = useCallback(
    error => {
      setErrorCode(oAuthErrorToErrorCode(error, channelType))
      // eslint-disable-next-line no-console
      console.error(error)
    },
    [channelType]
  )

  const handleGmailChannel = useCallback(
    data => {
      if (source === 'inbox') {
        return dispatch(doOauthGmailChannel(channelId))
      }
      return dispatch(doAddGmailChannel(data))
    },
    [dispatch, source, channelId]
  )

  const handleChannelOAuth = useCallback(
    data => {
      if (channelType === 'google') {
        return handleGmailChannel(data)
      }
      // add other oauth handles here

      return null
    },
    [channelType, handleGmailChannel]
  )

  const onSubmit = useCallback(
    async data => {
      try {
        setIsInstalling(true)
        setErrorCode(null)

        const {
          entities: {
            pending: { update: { channel: channelById } = {} } = {},
          } = {},
        } = dispatch(doSaveChannelDraft(channelId, data))

        const { id } = await handleChannelOAuth(channelById[channelId])

        openNextDrawer(id, {
          query: {
            ...buildDrawerQueryParam(
              nextDrawerId,
              'drawerChannelType',
              channelType
            ),
          },
        })
      } catch (error) {
        setIsInstalling(false)
        handleError(error)
      }
    },
    [
      dispatch,
      openNextDrawer,
      nextDrawerId,
      channelId,
      channelType,
      handleChannelOAuth,
      handleError,
    ]
  )

  const actionsComponentProps = useMemo(
    () => {
      return {
        formId,
        onClose: handleOnClose,
        channelType,
        isInstalling,
        errorCode,
      }
    },
    [formId, handleOnClose, channelType, isInstalling, errorCode]
  )

  const footerRef = useRef(null)

  return (
    <AdminAccessDrawer
      title={drawerTitle}
      footer={<div ref={footerRef} />}
      onClose={handleOnExit}
      data-test-id="channels-details-oauth"
    >
      <ChannelDetailsForm
        formId={formId}
        channelId={channelId}
        channelType={channelType}
        onSubmit={onSubmit}
        fullWidth
        actionsPortalRef={footerRef}
        actionsComponent={Actions}
        actionsComponentAdditionalProps={actionsComponentProps}
      />
    </AdminAccessDrawer>
  )
}

export default ChannelDetailsOAuth
